import {
  BillPayResponse,
  TransactionDetails,
  TransactionHistoryTableModel,
  TransactionResponse,
  Transfer,
  UpcomingTransactionModel,
  UpcomingTransactionsTableModel,
} from '../types'
import {
  amountFormatter,
  getServerTimeShortDate,
  maskAccountNumber,
  getServerDateWithAbbrName,
  formatDateForEST,
} from './formatter'
import { relativeDates } from './dates'

export const mapTransfers = (
  transfers: Transfer[] | undefined,
  accountId: string,
  accountNumberPvtEncrypt?: string,
): UpcomingTransactionModel[] => {
  if (!transfers) {
    return []
  }
  return transfers.reduce((acc, transfer) => {
    const {
      amount,
      fromAccount,
      toAccount,
      status,
      frequency,
      domainDetails,
      transactionType,
    } = transfer // option chaining branches

    /* istanbul ignore next */ const toAccountId =
      (toAccount && toAccount?.domainDetails?.accountId) || '' // option chaining branches
    /* istanbul ignore next */ const fromAccountId =
      (fromAccount && fromAccount?.domainDetails?.accountId) || ''

    /* istanbul ignore next */ const toAccountNumberPvtEncrypt =
      (toAccount && toAccount?.accountNumberPvtEncrypt) || '' // option chaining branches
    /* istanbul ignore next */ const fromAccountNumberPvtEncrypt =
      (fromAccount && fromAccount?.accountNumberPvtEncrypt) || ''

    const isInvest = domainDetails.domain === 'INVEST'

    const accountsMatch =
      accountId === toAccountId || accountId === fromAccountId

    const accountNumberMatch =
      accountNumberPvtEncrypt === toAccountNumberPvtEncrypt ||
      accountNumberPvtEncrypt === fromAccountNumberPvtEncrypt

    if (!accountsMatch && !accountNumberMatch) {
      return acc
    }

    const nonInvestDeliveryDate = domainDetails.deliveryDate // ex: 2021-11-15T16:40:23.26
    const investDeliveryDate = domainDetails.plan?.nextRunDate || '' // ex: 2021-11-15

    /* The effective date model differs between Invest and Non-Invest transfers.
           This function is used to determine and format the correct effective date based upon the type of transfer */

    const getInvestDeliveryDate = formatDateForEST(investDeliveryDate)
    const transactionEffectiveDate =
      nonInvestDeliveryDate || getInvestDeliveryDate
    let isCredit = toAccountId === accountId

    if (isInvest && transactionType === 'RTT_WITHDRAWL') {
      isCredit = true
    } // option chaining branches
    /* istanbul ignore next */ let nickName = isCredit
      ? fromAccount?.nickname
      : toAccount?.nickname
    nickName = isInvest ? 'Ally Invest' : nickName // option chaining branches

    /* istanbul ignore next */ const externalAccount = isCredit
      ? toAccount?.domainDetails?.externalAccountIndicator
      : fromAccount?.domainDetails?.externalAccountIndicator

    /* Updates the transactionType to trigger the recurring icon for invest transactions.
          This passes through the original transactionType unless the transaction is a
           one-time Invest transaction. */
    const type =
      isInvest && frequency !== 'ONE_TIME' ? 'Recurring' : transactionType
    return [
      ...acc,
      {
        transactionEffectiveDate,
        isCreditTransaction: isCredit,
        transactionAmount: amount,
        isBillPay: false,
        type,
        status,
        externalAccount,
        frequency,
        nickName,
        // option chaining branches
        maskedAccountNumber: /* istanbul ignore next */ isCredit
          ? fromAccount?.accountNumberPvtEncrypt
          : toAccount?.accountNumberPvtEncrypt,
        domain: domainDetails.domain,
      },
    ]
  }, [] as UpcomingTransactionModel[])
}

/* istanbul ignore next */ export const mapBillPay = (
  billPayments: BillPayResponse | undefined,
): UpcomingTransactionModel[] => {
  const transactions: UpcomingTransactionModel[] = []
  if (!billPayments || !billPayments.recordGroup) {
    return []
  }

  billPayments.recordGroup.forEach(group => {
    const { record } = group
    record.forEach(currRec => {
      const {
        paymentDate,
        amountPvtEncrypt,
        recurring,
        frequency,
        status,
        payee,
      } = currRec

      transactions.push({
        frequency,
        transactionEffectiveDate: paymentDate,
        isCreditTransaction: false,
        transactionAmount: amountPvtEncrypt,
        isBillPay: true,
        type: recurring ? 'Recurring' : null,
        status,
        nickName: payee.name || payee.nickname,
        maskedAccountNumber: payee.accountNumPvtEncrypt,
        domain: '',
        externalAccount: true,
      })
    })
  })
  return transactions.filter(
    payment =>
      payment &&
      // filter out any transactions > 30 days old
      new Date(
        getServerTimeShortDate(payment.transactionEffectiveDate),
      ).getTime() < new Date(relativeDates().thirtyDaysFuture).getTime(),
  )
}

/* istanbul ignore next */ const getTableDescription = (
  transaction: UpcomingTransactionModel,
): string => {
  let transferText
  const {
    isCreditTransaction,
    isBillPay,
    externalAccount,
    nickName,
    maskedAccountNumber,
  } = transaction

  if (isBillPay) {
    transferText = `BILLPAY TO`
  } else {
    transferText = `TRANSFER ${isCreditTransaction ? 'FROM' : 'TO'}`
  }

  const defaultNickname = externalAccount ? 'External Account' : ''

  const description = `${transferText.toUpperCase()} ${
    nickName || defaultNickname ? `${nickName || defaultNickname} ` : ''
  }${maskAccountNumber(maskedAccountNumber)}`.trim()

  return description
}

/* istanbul ignore next */ export const sortTransactionsByDate = (
  transactions: UpcomingTransactionsTableModel[],
): UpcomingTransactionsTableModel[] => {
  return transactions.sort((a, b) =>
    new Date(a.date) > new Date(b.date) ? 1 : -1,
  )
}

export const tableFormatter = (
  transactions: UpcomingTransactionModel[],
): UpcomingTransactionsTableModel[] =>
  transactions.reduce((acc, transaction: UpcomingTransactionModel) => {
    const {
      status,
      transactionEffectiveDate,
      transactionAmount,
      isCreditTransaction,
      type,
    } = transaction
    if (
      ['scheduled', 'in-process', 'in process', 'recurring'].includes(
        status.toLowerCase(),
      )
    ) {
      /* istanbul ignore next */ // option chaining branches
      return [
        ...acc,
        {
          amount: amountFormatter.format(transactionAmount),
          date: getServerDateWithAbbrName(transactionEffectiveDate),
          description: getTableDescription(transaction),
          isCredit: isCreditTransaction,
          status: `${status[0]}${status.slice(1).toLowerCase()}`,
          isRecurring: type?.toLowerCase() === 'recurring',
        },
      ]
    }
    return acc
  }, [] as UpcomingTransactionsTableModel[])

export const formatSearchedTransactions = (
  postedTransactions: TransactionResponse[] | any,
): TransactionHistoryTableModel[] => {
  const result: TransactionHistoryTableModel[] = []
  postedTransactions.forEach((transaction: any) => {
    const {
      checkTransactionIndicator,
      transactionAmountPvtEncrypt,
      transactionBalancePvtEncrypt,
      transactionDescription,
      transactionPostingDate,
      transactionId,
      transactionSequenceNumber,
      transactionCode,
    } = transaction

    result.push({
      id: transactionId,
      date: getServerTimeShortDate(transactionPostingDate),
      description: transactionDescription,
      amount: transactionAmountPvtEncrypt,
      balance: transactionBalancePvtEncrypt,
      sequenceNumber: transactionSequenceNumber,
      isCheck: checkTransactionIndicator,
      showEdd: transactionCode === 'ZEDIR',
    })
  })
  return result
}

export const mapTransactions = (
  transResp: TransactionResponse[],
): TransactionHistoryTableModel[] => {
  const result: TransactionHistoryTableModel[] = []
  const pendingTransactions: TransactionResponse = transResp[0]
  if (pendingTransactions && pendingTransactions.transactionHistory) {
    pendingTransactions.transactionHistory[0].transactions.forEach(
      transaction => {
        const {
          checkTransactionIndicator,
          transactionAmountPvtEncrypt,
          transactionBalancePvtEncrypt,
          transactionDescription,
          transactionId,
          transactionSequenceNumber,
          transactionCode,
          transactionStatus,
          transactionType,
          transactionEffectiveDate,
        } = transaction

        const isDepositedCheck =
          transactionStatus === 'Pending' && transactionType === 'Deposit'

        result.push({
          id: transactionId,
          date: 'Pending',
          description: isDepositedCheck
            ? 'Deposited Check'
            : transactionDescription,
          amount: transactionAmountPvtEncrypt,
          balance: transactionBalancePvtEncrypt,
          sequenceNumber: transactionSequenceNumber,
          isDepositedCheck,
          transactionEffectiveDate,
          isCheck: checkTransactionIndicator,
          showEdd: transactionCode === 'ZEDIR',
        })
      },
    )
  }
  const postedTransactions: TransactionResponse = transResp[1]
  if (postedTransactions && postedTransactions.transactionHistory) {
    postedTransactions.transactionHistory[0].transactions.forEach(
      transaction => {
        const {
          checkTransactionIndicator,
          transactionAmountPvtEncrypt,
          transactionBalancePvtEncrypt,
          transactionDescription,
          transactionPostingDate,
          transactionId,
          transactionSequenceNumber,
          transactionCode,
        } = transaction

        result.push({
          id: transactionId,
          date: getServerTimeShortDate(transactionPostingDate),
          description: transactionDescription,
          amount: transactionAmountPvtEncrypt,
          balance: transactionBalancePvtEncrypt,
          sequenceNumber: transactionSequenceNumber,
          isCheck: checkTransactionIndicator,
          showEdd: transactionCode === 'ZEDIR',
        })
      },
    )
  }
  return result
}

export const checkImageUrlComposer = (
  id: string,
  transactionDetails: TransactionDetails,
): TransactionDetails => {
  const { checkImageUrl, checkTransactionIndicator, transactionId } =
    transactionDetails
  if (!checkTransactionIndicator) return transactionDetails
  /* istanbul ignore next: ignored trim */
  const derivedCheckImageUrl = !checkImageUrl?.trim()
    ? `/accounts/${id}/transactions/${transactionId}/checkimages`
    : checkImageUrl
  return {
    ...transactionDetails,
    checkImageUrl: derivedCheckImageUrl,
  }
}

/* istanbul ignore next */
export const checkImageUrlComposerApigeeMigration = (
  id: string,
  transactionDetails: TransactionDetails,
): TransactionDetails => {
  const { checkTransactionIndicator, transactionId } = transactionDetails
  if (!checkTransactionIndicator) return transactionDetails

  const derivedCheckImageUrl = `/v1/bank-accounts/check-image/${id}/transaction/${transactionId}`

  return {
    ...transactionDetails,
    checkImageUrl: derivedCheckImageUrl,
  }
}
